import {
  Box,
  Button,
  Chip,
  colors,
  Divider,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';

import { IcDeleteTrash } from '@/assets/images';
import PersonPlaceholder from '@/assets/images/pngs/person-placeholder.png';
import { FlagChips } from '@/components';
import { useFetchSasToken } from '@/hooks';
import { IMasterNamePerson } from '@/models';
import { getAddressCommonString } from '@/services';
import {
  formatHeightDisplay,
  formatPersonName,
  getFileUrlWithValidToken,
} from '@/utils';

import { TabType } from '../tabs/CarStopCfsTab';

type PersonDetailsCardProps = {
  data: IMasterNamePerson;
  setSelectedTab: (tab: TabType) => void;
  onRemove?: () => void;
};

const DetailsItem = ({ label, value }: { label: string; value?: string }) => (
  <Stack
    display="flex"
    flexDirection="row"
    alignItems="center"
    columnGap={2}
    minHeight={32}
  >
    <Typography fontWeight={600} fontSize={16} width={220} flexShrink={0}>
      {label}
    </Typography>
    <Typography flex={1} color="text.secondary" fontSize={16}>
      {value}
    </Typography>
  </Stack>
);

const RelationsItems = ({ data, setSelectedTab }: PersonDetailsCardProps) => {
  const linked = {
    CFS: data?.linkedCFSs ?? null,
    Cases: null,
  };

  return (
    <Stack gap={0.5} flex={1}>
      {/* TODO: linked cases is missing */}
      {(['CFS', 'Cases'] as Array<keyof typeof linked>).map((item) => (
        <Stack
          key={item}
          display="flex"
          flexDirection="row"
          alignItems="center"
          minHeight={32}
        >
          <Typography fontWeight={600} fontSize={16} flex={1}>
            {item} {linked[item] && <Chip color="error" label={linked[item]} />}
          </Typography>
          <Box flex={1} display="flex" alignItems="center" minHeight={32}>
            {linked[item] && (
              <Button
                variant="text"
                onClick={() =>
                  item === 'CFS'
                    ? setSelectedTab('relations-cfs')
                    : setSelectedTab('relations-cases')
                }
              >
                View more
              </Button>
            )}
          </Box>
        </Stack>
      ))}
    </Stack>
  );
};

const PersonDetailsCard = ({
  data,
  setSelectedTab,
  onRemove,
}: PersonDetailsCardProps) => {
  const sasToken = useFetchSasToken(data.files);

  const personDetailsItems = [
    { label: 'ID number', value: data?.ssn || '/' },
    { label: 'Date of birth', value: data?.birthday || '/' },
    { label: 'Issued date', value: data?.driverLicense?.number || '/' },
    { label: 'Expired date', value: data?.driverLicense?.expireDate || '/' },
    { label: 'Sex', value: data?.sex || '/' },
    {
      label: 'Height',
      value: formatHeightDisplay(
        data?.physicalCharacteristics?.heightExact,
        '/',
      ),
    },
    { label: 'Eyes', value: data?.physicalCharacteristics?.eyeColor || '/' },
    {
      label: "Driver's license number",
      value: data?.driverLicense?.number || '/',
    },
  ];

  return (
    <Stack
      padding={2}
      border={1}
      borderRadius={1}
      borderColor={colors.grey[300]}
      position="relative"
      minHeight={560}
    >
      <Stack direction="row" alignItems="flex-start" gap={2}>
        <Stack gap={0.75} flex={1} minWidth={0}>
          <Stack direction="row" alignItems="flex-start" gap={2}>
            <Typography
              fontWeight={600}
              fontSize={24}
              sx={{ lineHeight: 1.15, wordBreak: 'break-word' }}
            >
              {formatPersonName(data)}
            </Typography>
            {data?.flags && data.flags.length > 0 && (
              <FlagChips flags={data.flags.map((f) => f.flag)} size="medium" />
            )}
          </Stack>
          <Typography
            fontWeight={400}
            fontSize={18}
            color="text.secondary"
            minHeight={32}
            sx={{
              lineHeight: 1.2,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {getAddressCommonString(data?.address) || ' '}
          </Typography>
        </Stack>
        <Stack direction="row" alignItems="flex-start" gap={1} flexShrink={0}>
          <img
            src={
              data?.files && data?.files?.length > 0
                ? `${getFileUrlWithValidToken(data.files[0], sasToken) ?? ''}`
                : PersonPlaceholder
            }
            height={84}
            width={84}
            style={{ objectFit: 'cover', borderRadius: 8 }}
          />
          {onRemove && (
            <IconButton
              size="small"
              onClick={onRemove}
              sx={{
                color: colors.red[500],
                backgroundColor: colors.red[50],
                '&:hover': {
                  backgroundColor: colors.red[100],
                },
              }}
            >
              <IcDeleteTrash />
            </IconButton>
          )}
        </Stack>
      </Stack>
      <Divider sx={{ marginY: 2 }} />
      <Stack flex={1} justifyContent="space-between">
        <Stack gap={1} height={312}>
          {personDetailsItems.map((item, index) => (
            <DetailsItem
              key={index}
              label={item.label}
              value={item.value as string}
            />
          ))}
        </Stack>
        <Stack>
          <Divider sx={{ marginY: 2 }} />
          <RelationsItems data={data} setSelectedTab={setSelectedTab} />
        </Stack>
      </Stack>
    </Stack>
  );
};

export default PersonDetailsCard;
